import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  EPOverview,
  EPData,
  EPChat,
  EPTools,
  EPTranscript,
} from '@rsos/assets/icons';
import { BE_END_CALL } from '@rsos/constants/callStatuses';
import { useFlag } from '@rsos/flags';
import { setEmergencyCallUI } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  setSelectedTab,
  resetState,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import {
  TOOLS_TAB,
  CHAT_TAB,
  OVERVIEW_TAB,
  DATA_TAB,
  TRANSCRIPT_TAB,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { useGetEventSharesByPhoneQuery } from '@rsos/reduxDataStore/src/features/eventShare/eventShareApi';
import { ROADSIDE_ASSISTANCE_FEATURE } from '@rsos/reduxDataStore/src/features/outboundSms/outboundSmsSlice';
import { dismissRcsToast } from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import getIsPrimaryChatTab from '@rsos/utils/getIsPrimaryChatTab';
import { RootState } from '../../../app';
import {
  trackTranscriptionTabOpen,
  trackTranscriptionTabDuration,
} from '../../../gainsightTrackings/callTranscriptionTrackings';
import {
  trackEventPaneOpen,
  trackEventPaneClose,
  trackEventPaneTabOpen,
} from '../../../gainsightTrackings/eventPaneTrackings';
import useFetchEmergencySessionForTel from '../../../hooks/useFetchEmergencySessionForTel';
import useHasAccessToCommunicatorTranscription from '../../../hooks/useHasAccessToCommunicatorTranscription';
import useHasAccessToFieldSharing from '../../../hooks/useHasAccessToFieldSharing';
import What3WordsSearchResult from '../../What3Words/What3WordsSearchResult';
import { useChatComponents } from '../useChatComponents';
import { EventPaneDock, StyledEventPane } from './EventPane.styles';
import EventPaneHeader from './EventPaneHeader';
import { TabSwitcher, Tab, TabLabel } from './EventPaneTabs';
import TranscriptHistoryPane from './TranscriptHistoryPane';
import ChatsTab from './tabs/ChatsTab';
import DataCards from './tabs/DataCards';
import OverviewTab from './tabs/OverviewTab';
import ToolsTab from './tabs/ToolsTab';
import TranscriptionTab from './tabs/TranscriptTab/TranscriptionTab';
import {
  selectPreviousTranscriptionsForCall,
  selectTranscriptionForCall,
  useFetchTranscriptionsQuery,
} from './tabs/TranscriptTab/transcriptionSlice';
import useCallDetails from './useCallDetails';

interface EventPaneProps {
  callerID: string;
  hasW3w?: boolean;
  onActionableButton: (payload: unknown) => void;
  onClose: () => void;
}

const EventPane = ({
  callerID,
  hasW3w = false,
  onActionableButton,
  onClose,
}: EventPaneProps) => {
  const dispatch = useDispatch();
  const showFieldSharing = useHasAccessToFieldSharing();
  const showRecentTranscripts = useFlag('show_recent_transcripts');
  const callStartTime = useSelector((state: RootState) => {
    const location0 =
      state?.emergencyCalls?.callQueue?.[callerID]?.core?.locations?.[0];
    if (!location0) return null;
    if ('conversation_start_time' in location0)
      return location0.conversation_start_time;
    if ('call_start_time' in location0) return location0.call_start_time;
    return null;
  });

  // Fire off a transcription fetch when the call changes, even if callerID is the same.
  useFetchTranscriptionsQuery({ callerId: callerID, callStartTime });

  useFetchEmergencySessionForTel(callerID);
  const orgName = useCurrentOrgName();

  // Fetch event shares for the current call
  useGetEventSharesByPhoneQuery(
    {
      org: orgName || '',
      phoneNumber: callerID ? `tel://${callerID}` : '',
    },
    {
      skip: !orgName || !callerID || !showFieldSharing,
    },
  );

  const [accordionHeight, setAccordionHeight] = useState(0);

  const callDetails = useCallDetails(callerID);
  const hasAccessToCommunicatorTranscript =
    useHasAccessToCommunicatorTranscription();
  const selectedFeature = useSelector(
    (state: RootState) => state.outboundSms.selectedFeature,
  );
  const selectedTab = useSelector(
    (state: RootState) => state.eventPane.selectedTab,
  );
  const isOnlyChatTab = getIsPrimaryChatTab(callDetails.lastLocation);

  const hideDataTab = callDetails.isAdmin || isOnlyChatTab;

  const hideTranscriptTab = !hasAccessToCommunicatorTranscript || isOnlyChatTab;

  const isFirstRender = useRef(true);
  const isProgrammaticTabSwitch = useRef(false);
  const transcriptionTabStartTime = useRef<number | null>(null);
  const transcriptionTabCallState = useRef<'ended' | 'active' | null>(null);

  const isCollapsed = useSelector(
    (state: RootState) => state.eventPane.isCollapsed,
  );
  const [isTranscriptHistoryOpen, setIsTranscriptHistoryOpen] = useState(false);

  const previousTranscriptions = useSelector((state: RootState) =>
    showRecentTranscripts
      ? selectPreviousTranscriptionsForCall(callDetails.callerID)(state)
      : [],
  );
  const currentTranscription = useSelector((state: RootState) =>
    selectTranscriptionForCall(callDetails.callerID)(state),
  );
  const totalRecords = useSelector(
    (state: RootState) =>
      state?.transcriptions?.transcriptionTotals?.[callDetails.callerID],
  );

  const seededCallStartTime =
    currentTranscription?.payload?.call_information?.call_start_time || null;
  const hasCurrentTranscriptionSeeded =
    typeof callStartTime === 'number' &&
    typeof seededCallStartTime === 'number' &&
    seededCallStartTime > 0 &&
    Math.abs(callStartTime - seededCallStartTime) < 10 * 60 * 1000;
  const adjustedTotalCount =
    typeof totalRecords === 'number'
      ? Math.max(0, totalRecords - (hasCurrentTranscriptionSeeded ? 1 : 0))
      : null;
  const previousTotalCount =
    adjustedTotalCount === null
      ? previousTranscriptions.length
      : Math.max(adjustedTotalCount, previousTranscriptions.length);
  const canOpenRecentTranscripts =
    showRecentTranscripts &&
    hasAccessToCommunicatorTranscript &&
    previousTotalCount > 0;

  const handleOpenRecentTranscripts = () => {
    if (!canOpenRecentTranscripts) return;
    setIsTranscriptHistoryOpen(prev => !prev);
  };

  const shouldShowTranscriptHistoryPane =
    !isCollapsed && isTranscriptHistoryOpen && showRecentTranscripts;

  // Track EventPane open when component mounts
  useEffect(() => {
    trackEventPaneOpen();
  }, []);

  // Track EventPane close when component unmounts (covers all close scenarios)
  useEffect(() => {
    return () => {
      // Track linger time if EventPane closes while on Transcription tab
      if (
        transcriptionTabStartTime.current !== null &&
        transcriptionTabCallState.current !== null
      ) {
        const durationSeconds =
          (Date.now() - transcriptionTabStartTime.current) / 1000;

        trackTranscriptionTabDuration({
          callState: transcriptionTabCallState.current,
          durationSeconds,
        });
      }
      trackEventPaneClose();
    };
  }, []);

  // Track tab changes (skip initial render and programmatic switches)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (isProgrammaticTabSwitch.current) {
      isProgrammaticTabSwitch.current = false;
      return;
    }

    trackEventPaneTabOpen({ tabId: selectedTab });

    // Track linger time when leaving the Transcription tab
    if (
      transcriptionTabStartTime.current !== null &&
      transcriptionTabCallState.current !== null &&
      selectedTab !== TRANSCRIPT_TAB
    ) {
      const durationSeconds =
        (Date.now() - transcriptionTabStartTime.current) / 1000;

      trackTranscriptionTabDuration({
        callState: transcriptionTabCallState.current,
        durationSeconds,
      });
      transcriptionTabStartTime.current = null;
      transcriptionTabCallState.current = null;
    }

    // Track when entering the Transcription tab
    if (selectedTab === TRANSCRIPT_TAB) {
      const callState =
        callDetails.callStatus === BE_END_CALL ? 'ended' : 'active';

      trackTranscriptionTabOpen({ callState });
      transcriptionTabStartTime.current = Date.now();
      transcriptionTabCallState.current = callState;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  useEffect(() => {
    dispatch(dismissRcsToast({ callerID }));
    if (selectedFeature === ROADSIDE_ASSISTANCE_FEATURE) {
      isProgrammaticTabSwitch.current = true;
      dispatch(setSelectedTab(CHAT_TAB));
    }
  }, [callerID, dispatch, selectedFeature]);

  useEffect(() => {
    // Switching calls should always close the history pane.
    setIsTranscriptHistoryOpen(false);
  }, [callerID]);

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedTab === CHAT_TAB) {
      dispatch(
        setEmergencyCallUI({
          callerID: callDetails.callerID,
          ui: {
            requestedLocationReceived: false,
          },
        }),
      );
    }
  }, [selectedTab, callDetails.callerID, dispatch]);

  const { isChatDisabled, chatComponents } = useChatComponents(callerID);

  const hasChatSession = Object.values(chatComponents).some(
    chat => chat && (chat.isActive || chat.lastUpdated > 0),
  );

  // When a call with a chat session (active or ended, but not blank-slate) is selected,
  // open the Chat tab automatically.
  // The isProgrammaticTabSwitch flag prevents this from being recorded as a user-initiated tab change.
  useEffect(() => {
    if (hasChatSession && selectedTab !== CHAT_TAB) {
      isProgrammaticTabSwitch.current = true;
      dispatch(setSelectedTab(CHAT_TAB));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callerID]);

  return (
    <EventPaneDock>
      <StyledEventPane isCollapsed={isCollapsed}>
        {hasW3w && (
          <What3WordsSearchResult
            hasW3w={hasW3w}
            setAccordionHeight={setAccordionHeight}
            accordionHeight={accordionHeight}
          />
        )}
        <EventPaneHeader
          callerID={callDetails.callerID}
          source={callDetails.source ?? ''}
          timestamp={callDetails.lastTimestamp ?? 0}
          recentTranscriptionsCount={previousTotalCount}
          onCallerIdClick={
            canOpenRecentTranscripts ? handleOpenRecentTranscripts : undefined
          }
          onClose={onClose}
        />
        {!isCollapsed && (
          <TabSwitcher>
            <Tab
              id={OVERVIEW_TAB}
              data-name="overviewTab"
              Label={<TabLabel Icon={EPOverview} text="Overview" />}
            >
              <OverviewTab
                onDataCardClick={() => {
                  dispatch(setSelectedTab('data'));
                }}
                callerID={callerID}
                onOpenRecentTranscripts={handleOpenRecentTranscripts}
              />
            </Tab>
            <Tab
              id={DATA_TAB}
              data-name="dataTab"
              Label={<TabLabel Icon={EPData} text="Data" />}
              hidden={hideDataTab}
            >
              <DataCards
                onActionableButton={(payload?: unknown) =>
                  onActionableButton(payload)
                }
              />
            </Tab>
            <Tab
              id={TRANSCRIPT_TAB}
              data-name="transcriptTab"
              hidden={hideTranscriptTab}
              Label={<TabLabel Icon={EPTranscript} text="Transcript" />}
            >
              <TranscriptionTab callerId={callDetails.callerID} />
            </Tab>
            <Tab
              id={CHAT_TAB}
              data-name="chatTab"
              Label={<TabLabel Icon={EPChat} text="Chat" />}
              disabled={isChatDisabled}
            >
              <ChatsTab />
            </Tab>
            {
              <Tab
                id={TOOLS_TAB}
                Label={<TabLabel Icon={EPTools} text="Tools" />}
              >
                <ToolsTab />
              </Tab>
            }
          </TabSwitcher>
        )}
      </StyledEventPane>

      {shouldShowTranscriptHistoryPane && (
        <TranscriptHistoryPane
          callerId={callDetails.callerID}
          callStartTime={callStartTime}
          isOpen={isTranscriptHistoryOpen}
          onClose={() => setIsTranscriptHistoryOpen(false)}
          transcriptions={previousTranscriptions}
        />
      )}
    </EventPaneDock>
  );
};

export default EventPane;
